import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';
import './MyFavoritesScreen.css';

interface FavoriteStock {
  symbol: string;
  company_name: string;
  sector: string;
  added_at: string;
  latest_price: number | null;
  variation_pct: number | null;
  weekly_variation_pct: number | null;
}

const MyFavoritesScreen: React.FC = () => {
  const { token } = useAuth();
  const [favorites, setFavorites] = useState<FavoriteStock[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [imgErrorMap, setImgErrorMap] = useState<Record<string, boolean>>({});

  const handleImgError = (symbol: string) => {
    setImgErrorMap(prev => ({ ...prev, [symbol]: true }));
  };

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    if (!token) {
      setError('Non authentifié');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/favorites`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch favorites');

      const data = await response.json();
      setFavorites(data);
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading favorites');
      setLoading(false);
    }
  };

  const handleRemoveFavorite = async (symbol: string) => {
    if (!token) return;

    // Optimistic removal from UI state immediately
    setFavorites((prev) => prev.filter((stock) => stock.symbol !== symbol));

    try {
      const response = await fetch(`${API_URL}/api/favorites/${symbol}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to delete favorite');
      }
    } catch (error) {
      console.error('Error removing favorite:', error);
      // Rollback state on error
      fetchFavorites();
    }
  };

  if (loading) {
    return (
      <SiteLayout>
        <Header icon='star' title='Mes Favoris' />
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <p>Chargement...</p>
        </div>
      </SiteLayout>
    );
  }

  if (error) {
    return (
      <SiteLayout>
        <Header icon='star' title='Mes Favoris' />
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <p>{error}</p>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <Header icon='star' title='Mes Favoris' />
      <div className='favorites-container'>
        {favorites.length === 0 ? (
          <Box>
            <div
              className='box-content box-horizontal-padding'
              style={{ padding: '60px', textAlign: 'center' }}
            >
              <div style={{ fontSize: '48px', marginBottom: '20px' }}>⭐</div>
              <h2 style={{ marginBottom: '10px', color: 'var(--text-heading)' }}>Aucun favori pour le moment</h2>
              <p style={{ color: '#666', fontSize: '16px', marginBottom: '20px' }}>
                Ajoutez vos actions préférées depuis la page Capital
              </p>
              <Link to='/capital'>
                <button
                  style={{
                    padding: '12px 24px',
                    background: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold',
                  }}
                >
                  Aller à Capital
                </button>
              </Link>
            </div>
          </Box>
        ) : (
          <Box>
            <div className='box-title box-vertical-padding box-horizontal-padding'>
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <h3>
                  {favorites.length} action{favorites.length > 1 ? 's' : ''} favorite
                  {favorites.length > 1 ? 's' : ''}
                </h3>
              </div>
            </div>
            <div className='favorites-table-container'>
              <table className='favorites-table'>
                <thead>
                  <tr>
                    <th>Symbole</th>
                    <th>Société</th>
                    <th style={{ textAlign: 'right' }}>Prix</th>
                    <th style={{ textAlign: 'right' }}>Var. Jour</th>
                    <th style={{ textAlign: 'right' }}>Var. Hebdo</th>
                    <th style={{ textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {favorites.map((stock) => (
                    <tr key={stock.symbol}>
                      <td data-label='Symbole'>
                        <Link
                          to={`/data?symbol=${stock.symbol}`}
                          style={{ textDecoration: 'none', color: 'inherit', fontWeight: 'bold' }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            {!imgErrorMap[stock.symbol] ? (
                              <img
                                src={`/images/societes/${stock.symbol}.png`}
                                alt={stock.symbol}
                                onError={() => handleImgError(stock.symbol)}
                                style={{
                                  width: '36px',
                                  height: '36px',
                                  borderRadius: '50%',
                                  marginRight: '12px',
                                  objectFit: 'contain',
                                  backgroundColor: '#fff',
                                  padding: '4px',
                                  border: '1px solid var(--border-color)'
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '36px',
                                  height: '36px',
                                  borderRadius: '50%',
                                  background: 'var(--bg-input)',
                                  color: 'var(--text-main)',
                                  marginRight: '12px',
                                  fontSize: '12px',
                                  border: '1px solid var(--border-color)'
                                }}
                              >
                                {stock.symbol}
                              </div>
                            )}
                            {stock.symbol}
                          </div>
                        </Link>
                      </td>
                      <td data-label='Société'>
                        <div style={{ fontWeight: '500' }}>{stock.company_name}</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{stock.sector}</div>
                      </td>
                      <td data-label='Prix' style={{ textAlign: 'right', fontWeight: 'bold' }}>
                        {stock.latest_price ? `${stock.latest_price.toLocaleString()} FCFA` : 'N/A'}
                      </td>
                      <td data-label='Var. Jour' style={{ textAlign: 'right' }}>
                        {stock.variation_pct !== null ? (
                          <span
                            style={{
                              color: stock.variation_pct >= 0 ? '#4ade80' : '#f87171',
                              fontWeight: 'bold',
                              background: stock.variation_pct >= 0 ? 'rgba(22, 163, 74, 0.15)' : 'rgba(220, 38, 38, 0.15)',
                              padding: '4px 8px',
                              borderRadius: '4px',
                            }}
                          >
                            {stock.variation_pct > 0 ? '+' : ''}
                            {stock.variation_pct.toFixed(2)}%
                          </span>
                        ) : (
                          'N/A'
                        )}
                      </td>
                      <td data-label='Var. Hebdo' style={{ textAlign: 'right' }}>
                        {stock.weekly_variation_pct !== null ? (
                          <span
                            style={{
                              color: stock.weekly_variation_pct >= 0 ? '#4ade80' : '#f87171',
                              fontWeight: 'bold',
                            }}
                          >
                            {stock.weekly_variation_pct > 0 ? '+' : ''}
                            {stock.weekly_variation_pct.toFixed(2)}%
                          </span>
                        ) : (
                          'N/A'
                        )}
                      </td>
                      <td data-label='Actions' style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                          <Link to={`/stock/${stock.symbol}`}>
                            <button
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#667eea',
                                padding: '4px',
                              }}
                              title='Voir détails'
                            >
                              <i className='material-icons'>visibility</i>
                            </button>
                          </Link>
                          <button
                            onClick={() => handleRemoveFavorite(stock.symbol)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#fbbf24',
                              padding: '4px',
                            }}
                            title='Retirer des favoris'
                          >
                            <i className='material-icons'>star</i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Box>
        )}
      </div>
    </SiteLayout>
  );
};

export default MyFavoritesScreen;

